import { useState, type MouseEvent } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChecklistQuestion, useChecklistStore } from '../../../../stores/checklistStore';
import { TrashIcon, PencilIcon, CheckIcon, ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import { Button, TextInput } from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const SortableQuestionItem = ({
    question,
    variant = 'default',
}: {
    question: ChecklistQuestion;
    variant?: 'default' | 'onboarding';
}) => {
    const { updateQuestion, deleteQuestion } = useChecklistStore();
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(question.text);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: question.id,
        data: { type: 'Question', question }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const handleSave = () => {
        if (editText.trim() !== question.text) {
            updateQuestion(question.id, { text: editText });
        }
        setIsEditing(false);
    };

    const confirmDeleteQuestion = () => {
        const confirmed = window.confirm(
            t('checklistCustomization.modals.deleteQuestionMessage', {
                defaultValue: 'Are you sure you want to delete this question?',
            })
        );

        if (confirmed) {
            void deleteQuestion(question.id, question.center_id);
        }
    };

    if (variant === 'onboarding') {
        return (
            <div
                ref={setNodeRef}
                style={style}
                dir="rtl"
                className="group flex items-center gap-3 rounded-xl border border-neutral-200 bg-white px-4 py-3 shadow-[0px_1px_2px_rgba(0,0,0,0.03)] transition-colors hover:border-neutral-300 dark:border-neutral-700 dark:bg-neutral-800 dark:hover:border-neutral-600"
            >
                {isEditing ? (
                    <div className="flex flex-1 gap-2">
                        <TextInput
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="flex-1"
                            sizing="sm"
                            theme={{ field: { input: { base: 'w-full rounded-lg border-neutral-300 bg-neutral-50 p-1.5 text-right text-sm focus:border-brand-500 focus:ring-brand-500' } } }}
                        />
                        <Button size="xs" color="success" onClick={handleSave} className="rounded-lg">
                            <CheckIcon className="h-3 w-3" />
                        </Button>
                    </div>
                ) : (
                    <span className="flex-1 text-right text-[14px] font-medium leading-5 text-neutral-900 dark:text-neutral-100">
                        {question.text}
                    </span>
                )}

                {/* Actions — hidden by default, shown on hover */}
                <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button size="xs" color="light" pill onClick={() => setIsEditing(!isEditing)} className="border-none p-1 hover:bg-neutral-100 dark:bg-neutral-700 dark:hover:bg-neutral-600">
                        <PencilIcon className="h-3 w-3 text-neutral-500" />
                    </Button>
                    <Button
                        size="xs"
                        color="light"
                        pill
                        onClick={(event: MouseEvent<HTMLButtonElement>) => {
                            (event.currentTarget as HTMLButtonElement).blur();
                            confirmDeleteQuestion();
                        }}
                        className="border-none p-1 hover:bg-neutral-100 dark:bg-neutral-700 dark:hover:bg-neutral-600"
                    >
                        <TrashIcon className="h-3 w-3 text-red-500" />
                    </Button>
                </div>

                {/* Drag handle — last in DOM = leftmost in RTL */}
                <div
                    {...attributes}
                    {...listeners}
                    className="cursor-move rounded p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-brand-600 dark:hover:bg-neutral-700 dark:hover:text-brand-300"
                >
                    <ArrowsUpDownIcon className="h-4 w-4" />
                </div>
            </div>
        );
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="group flex items-center rounded-lg border border-neutral-200 bg-white p-2 shadow-sm transition-colors hover:border-brand-300 dark:border-neutral-700 dark:bg-neutral-800 dark:hover:border-brand-700"
        >
            <div className="ml-2 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <Button size="xs" color="light" pill onClick={() => setIsEditing(!isEditing)} className="border-none p-1 hover:bg-neutral-100 dark:bg-neutral-700 dark:hover:bg-neutral-600">
                    <PencilIcon className="h-3 w-3 text-neutral-500" />
                </Button>
                <Button
                    size="xs"
                    color="light"
                    pill
                    onClick={() => setIsDeleteModalOpen(true)}
                    className="border-none p-1 hover:bg-neutral-100 dark:bg-neutral-700 dark:hover:bg-neutral-600"
                >
                    <TrashIcon className="h-3 w-3 text-red-500" />
                </Button>
            </div>
            {isEditing ? (
                <div className="flex flex-1 gap-2">
                    <TextInput
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="flex-1"
                        sizing="sm"
                        theme={{ field: { input: { base: 'w-full rounded-lg border-neutral-300 bg-neutral-50 p-1.5 text-right text-sm focus:border-brand-500 focus:ring-brand-500' } } }}
                    />
                    <Button size="xs" color="success" onClick={handleSave} className="rounded-lg">
                        <CheckIcon className="h-3 w-3" />
                    </Button>
                </div>
            ) : (
                <div className="flex-1 text-sm text-gray-700 dark:text-gray-200">
                    {question.text}
                </div>
            )}
            <div
                {...attributes}
                {...listeners}
                className="mr-2 cursor-move rounded p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-brand-500 dark:hover:bg-neutral-700"
            >
                <ArrowsUpDownIcon className="h-4 w-4" />
            </div>

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={() => {
                    void deleteQuestion(question.id, question.center_id);
                    setIsDeleteModalOpen(false);
                }}
                title={t('checklistCustomization.modals.deleteQuestionTitle', { defaultValue: 'Delete Question' })}
                message={t('checklistCustomization.modals.deleteQuestionMessage', { defaultValue: 'Are you sure you want to delete this question?' })}
            />
        </div>
    );
};


export default SortableQuestionItem;
